<template>
  <div id="test-pack-detail">

    <!-- Back Button -->
    <div class="mb-3">
      <b-button variant="outline-primary" size="sm" @click="$router.push('/admin/tests')">
        <feather-icon icon="ArrowLeftIcon" size="14" class="mr-1"/>
        Back to Test Packs
      </b-button>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="text-center p-5">
      <b-spinner variant="primary" label="Loading..."></b-spinner>
      <p class="text-muted mt-3">Loading test pack details...</p>
    </div>

    <!-- Test Pack Not Found -->
    <div v-else-if="!testPack" class="text-center p-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3"/>
      <h4 class="text-danger">Test Pack Not Found</h4>
      <p class="text-muted">The requested test pack could not be found.</p>
      <b-button variant="primary" @click="$router.push('/admin/tests')">
        Go Back to Test Packs
      </b-button>
    </div>

    <!-- Test Pack Details -->
    <div v-else>

      <!-- Header Card -->
      <b-card class="test-pack-header mb-4">
        <div class="d-flex justify-content-between align-items-start">
          <div class="flex-grow-1">
            <div class="d-flex align-items-center mb-3">
              <div class="test-pack-icon-large mr-3">
                <feather-icon
                  :icon="getTestPackIcon(testPack.difficulty_level)"
                  :class="getDifficultyClass(testPack.difficulty_level)"
                  size="32"
                />
              </div>
              <div>
                <h2 class="mb-1">{{ testPack.name }}</h2>
                <p class="text-muted mb-0" v-if="testPack.description">{{ testPack.description }}</p>
              </div>
            </div>

            <!-- Meta Info -->
            <div class="meta-info">
              <b-row>
                <b-col md="3" sm="6" class="mb-2">
                  <div class="meta-item">
                    <small class="text-muted d-block">Subject</small>
                    <b-badge variant="outline-info" v-if="testPack.subject">
                      {{ testPack.subject.name }}
                    </b-badge>
                    <span v-else class="text-muted">No Subject</span>
                  </div>
                </b-col>
                <b-col md="3" sm="6" class="mb-2">
                  <div class="meta-item">
                    <small class="text-muted d-block">Difficulty</small>
                    <b-badge :variant="getDifficultyVariant(testPack.difficulty_level)">
                      {{ testPack.difficulty_level | capitalize }}
                    </b-badge>
                  </div>
                </b-col>
                <b-col md="3" sm="6" class="mb-2">
                  <div class="meta-item">
                    <small class="text-muted d-block">Status</small>
                    <b-badge :variant="getStatusVariant(testPack.status)">
                      <feather-icon :icon="getStatusIcon(testPack.status)" size="12" class="mr-1"/>
                      {{ testPack.status | capitalize }}
                    </b-badge>
                  </div>
                </b-col>
                <b-col md="3" sm="6" class="mb-2">
                  <div class="meta-item">
                    <small class="text-muted d-block">Created</small>
                    <span>{{ testPack.created_at | formatDate }}</span>
                  </div>
                </b-col>
              </b-row>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="action-buttons ml-4">
            <b-button
              variant="outline-primary"
              class="mb-2"
              block
              @click="editTestPack"
            >
              <feather-icon icon="EditIcon" size="14" class="mr-1"/>
              Edit
            </b-button>

            <b-button
              variant="outline-secondary"
              class="mb-2"
              block
              @click="duplicateTestPack"
            >
              <feather-icon icon="CopyIcon" size="14" class="mr-1"/>
              Duplicate
            </b-button>

            <b-button
              variant="outline-info"
              class="mb-2"
              block
              @click="exportQuestions"
            >
              <feather-icon icon="DownloadIcon" size="14" class="mr-1"/>
              Export
            </b-button>

            <b-button
              :variant="testPack.status === 'active' ? 'outline-warning' : 'outline-success'"
              class="mb-2"
              block
              @click="toggleStatus"
            >
              <feather-icon
                :icon="testPack.status === 'active' ? 'PauseIcon' : 'PlayIcon'"
                size="14"
                class="mr-1"
              />
              {{ testPack.status === 'active' ? 'Deactivate' : 'Activate' }}
            </b-button>

            <b-button
              variant="outline-danger"
              class="mb-2"
              block
              @click="deleteTestPack"
            >
              <feather-icon icon="ArchiveIcon" size="14" class="mr-1"/>
              Archive
            </b-button>

            <b-button
              variant="danger"
              block
              @click="forceDeleteTestPack"
            >
              <feather-icon icon="Trash2Icon" size="14" class="mr-1"/>
              Delete Forever
            </b-button>
          </div>
        </div>
      </b-card>

      <!-- Stats Cards -->
      <b-row class="mb-4">
        <b-col md="3" sm="6">
          <b-card class="stats-card">
            <div class="d-flex align-items-center">
              <div class="stats-icon bg-primary">
                <feather-icon icon="HelpCircleIcon" class="text-white"/>
              </div>
              <div class="stats-content">
                <h3 class="stats-number text-primary">{{ (testPack.questions || []).length }}</h3>
                <p class="stats-text mb-0">Questions</p>
              </div>
            </div>
          </b-card>
        </b-col>

        <b-col md="3" sm="6">
          <b-card class="stats-card">
            <div class="d-flex align-items-center">
              <div class="stats-icon bg-success">
                <feather-icon icon="AwardIcon" class="text-white"/>
              </div>
              <div class="stats-content">
                <h3 class="stats-number text-success">{{ testPack.total_points || 0 }}</h3>
                <p class="stats-text mb-0">Total Points</p>
              </div>
            </div>
          </b-card>
        </b-col>

        <b-col md="3" sm="6">
          <b-card class="stats-card">
            <div class="d-flex align-items-center">
              <div class="stats-icon bg-warning">
                <feather-icon icon="ClockIcon" class="text-white"/>
              </div>
              <div class="stats-content">
                <h3 class="stats-number text-warning">{{ testPack.time || 0 }}</h3>
                <p class="stats-text mb-0">Minutes</p>
              </div>
            </div>
          </b-card>
        </b-col>

        <b-col md="3" sm="6">
          <b-card class="stats-card">
            <div class="d-flex align-items-center">
              <div class="stats-icon bg-info">
                <feather-icon icon="TargetIcon" class="text-white"/>
              </div>
              <div class="stats-content">
                <h3 class="stats-number text-info">{{ averagePointsPerQuestion }}</h3>
                <p class="stats-text mb-0">Avg Points/Q</p>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Questions Section -->
      <QuestionsManager
        v-if="testPack"
        :test-pack="testPack"
        @question-created="refreshTestPack"
        @question-updated="refreshTestPack"
        @question-deleted="refreshTestPack"
        :show-back-button="false"
      />

    </div>

    <!-- Test Pack Edit Modal -->
    <TestPackModal
      :visible="showTestPackModal"
      :test-pack="testPack"
      :subjects="subjects"
      @save="onTestPackSaved"
      @close="showTestPackModal = false"
    />

  </div>
</template>

<script>
import {
  BButton,
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BBadge,
  BSpinner
} from 'bootstrap-vue'
import TestManagementApi from '@/api/testManagement'
import TestPackModal from './components/TestPackModal.vue'
import QuestionsManager from './components/QuestionsManager.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'TestPackDetail',

  components: {
    BButton,
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BBadge,
    BSpinner,
    TestPackModal,
    QuestionsManager,
    ToastificationContent
  },

  data() {
    return {
      testPack: null,
      subjects: [],
      isLoading: false,
      showTestPackModal: false
    }
  },

  computed: {
    testPackId() {
      return this.$route.params.id
    },

    averagePointsPerQuestion() {
      const questions = this.testPack && this.testPack.questions ? this.testPack.questions : []
      if (questions.length === 0) return 0
      const totalPoints = questions.reduce((sum, q) => sum + (parseFloat(q.points) || 0), 0)
      return Math.round((totalPoints / questions.length) * 10) / 10
    }
  },

  filters: {
    capitalize(value) {
      if (!value) return ''
      return value.charAt(0).toUpperCase() + value.slice(1)
    },

    formatDate(value) {
      if (!value) return ''
      return new Date(value).toLocaleDateString()
    }
  },

  watch: {
    '$route.params.id': {
      handler(newId, oldId) {
        if (newId && newId !== oldId) {
          this.init()
        }
      },
      immediate: false
    }
  },

  mounted() {
    this.init()
  },

  methods: {
    async init() {
      await Promise.all([
        this.loadTestPack(),
        this.loadSubjects()
      ])
    },

    async loadTestPack() {
      try {
        this.isLoading = true
        const api = new TestManagementApi()
        const response = await api.getTestPackQuestions(this.testPackId)
        this.testPack = response.data.test_pack
      } catch (error) {
        console.error('Error loading test pack:', error)
        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            text: 'Failed to load test pack details',
            variant: 'danger',
          },
        })
      } finally {
        this.isLoading = false
      }
    },

    async loadSubjects() {
      try {
        const api = new TestManagementApi()
        const response = await api.getSubjects()
        this.subjects = response.data || []
      } catch (error) {
        console.error('Error loading subjects:', error)
      }
    },

    async refreshTestPack() {
      await this.loadTestPack()
    },

    // Actions
    editTestPack() {
      this.showTestPackModal = true
    },

    async duplicateTestPack() {
      try {
        const result = await this.$bvModal.msgBoxConfirm(
          `Are you sure you want to duplicate the test pack "${this.testPack.name}"?`,
          {
            title: 'Confirm Duplication',
            size: 'md',
            okVariant: 'primary',
            okTitle: 'Duplicate',
            cancelTitle: 'Cancel'
          }
        )

        if (result) {
          this.isLoading = true
          const api = new TestManagementApi()
          const response = await api.duplicateTestPack(this.testPack.id)

          this.$toast({
            component: 'ToastificationContent',
            props: {
              title: 'Success',
              icon: 'CheckIcon',
              text: `Test pack duplicated successfully as "${response.data.name}"`,
              variant: 'success',
            },
          })

          // Navigate to the new duplicated test pack
          if (response.data && response.data.id) {
            // Use replace to avoid duplicate history entries, then push
            await this.$router.replace(`/admin/tests/${response.data.id}`)
          }

          this.isLoading = false
        }
      } catch (error) {
        console.error('Error duplicating test pack:', error)
        this.isLoading = false
        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            text: 'Failed to duplicate test pack',
            variant: 'danger',
          },
        })
      }
    },

    async exportQuestions() {
      try {
        const api = new TestManagementApi()
        const response = await api.exportQuestions(this.testPack.id)

        // Download the JSON file
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${this.testPack.name.replace(/[^a-zA-Z0-9]/g, '_')}_questions.json`
        link.click()
        window.URL.revokeObjectURL(url)

        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Success',
            icon: 'DownloadIcon',
            text: 'Questions exported successfully',
            variant: 'success',
          },
        })
      } catch (error) {
        console.error('Error exporting questions:', error)
        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            text: 'Failed to export questions',
            variant: 'danger',
          },
        })
      }
    },

    async toggleStatus() {
      try {
        const newStatus = this.testPack.status === 'active' ? 'inactive' : 'active'
        const api = new TestManagementApi()

        // Only send the fields that the backend expects
        const updateData = {
          name: this.testPack.name,
          description: this.testPack.description,
          subject_id: this.testPack.subject ? this.testPack.subject.id : this.testPack.subject_id,
          difficulty_level: this.testPack.difficulty_level,
          time_limit: this.testPack.time,
          total_points: this.testPack.total_points,
          instructions: this.testPack.instructions,
          is_randomized: this.testPack.settings && this.testPack.settings.is_randomized ? this.testPack.settings.is_randomized : false,
          show_results_immediately: this.testPack.settings && this.testPack.settings.show_results_immediately ? this.testPack.settings.show_results_immediately : true,
          allow_retakes: this.testPack.settings && this.testPack.settings.allow_retakes ? this.testPack.settings.allow_retakes : false,
          max_attempts: this.testPack.settings && this.testPack.settings.max_attempts ? this.testPack.settings.max_attempts : 1,
          passing_score: this.testPack.settings && this.testPack.settings.passing_score ? this.testPack.settings.passing_score : 60,
          tags: this.testPack.settings && this.testPack.settings.tags ? JSON.stringify(this.testPack.settings.tags) : null,
          status: newStatus
        }

        await api.updateTestPack(this.testPack.id, updateData)

        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Success',
            icon: 'CheckIcon',
            text: `Test pack ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`,
            variant: 'success',
          },
        })

        await this.refreshTestPack()
      } catch (error) {
        console.error('Error updating test pack status:', error)
        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            text: 'Failed to update test pack status',
            variant: 'danger',
          },
        })
      }
    },

    async deleteTestPack() {
      try {
        const result = await this.$bvModal.msgBoxConfirm(
          `Are you sure you want to archive the test pack "${this.testPack.name}"? You can restore it later.`,
          {
            title: 'Confirm Archive',
            size: 'md',
            okVariant: 'warning',
            okTitle: 'Archive',
            cancelTitle: 'Cancel'
          }
        )

        if (result) {
          const api = new TestManagementApi()
          await api.deleteTestPack(this.testPack.id)

          this.$toast({
            component: 'ToastificationContent',
            props: {
              title: 'Success',
              icon: 'CheckIcon',
              text: 'Test pack archived successfully',
              variant: 'success',
            },
          })

          // Navigate back to test packs list
          this.$router.push('/admin/tests')
        }
      } catch (error) {
        console.error('Error archiving test pack:', error)
        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            text: (error.response && error.response.data && error.response.data.error) || 'Failed to archive test pack',
            variant: 'danger',
          },
        })
      }
    },

    async forceDeleteTestPack() {
      try {
        const result = await this.$bvModal.msgBoxConfirm(
          `⚠️ WARNING: Are you absolutely sure you want to PERMANENTLY DELETE the test pack "${this.testPack.name}"?\n\nThis will:\n• Delete the test pack forever\n• Delete all associated questions\n• Delete all question images and audio files\n\nThis action CANNOT be undone!`,
          {
            title: 'Permanently Delete Test Pack',
            size: 'md',
            okVariant: 'danger',
            okTitle: 'Yes, Delete Forever',
            cancelTitle: 'Cancel',
            centered: true
          }
        )

        if (result) {
          this.isLoading = true
          const api = new TestManagementApi()
          await api.forceDeleteTestPack(this.testPack.id)

          this.$toast({
            component: 'ToastificationContent',
            props: {
              title: 'Success',
              icon: 'CheckIcon',
              text: 'Test pack permanently deleted successfully',
              variant: 'success',
            },
          })

          // Navigate back to test packs list
          this.$router.push('/admin/tests')
        }
      } catch (error) {
        console.error('Error permanently deleting test pack:', error)
        this.isLoading = false
        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            text: (error.response && error.response.data && error.response.data.error) || 'Failed to permanently delete test pack',
            variant: 'danger',
          },
        })
      }
    },

    // Event Handlers
    async onTestPackSaved() {
      this.showTestPackModal = false
      await this.refreshTestPack()
    },

    // Helper methods
    getTestPackIcon(difficulty) {
      switch (difficulty) {
        case 'beginner': return 'BookIcon'
        case 'intermediate': return 'BookOpenIcon'
        case 'advanced': return 'AwardIcon'
        default: return 'HelpCircleIcon'
      }
    },

    getDifficultyClass(difficulty) {
      switch (difficulty) {
        case 'beginner': return 'text-success'
        case 'intermediate': return 'text-warning'
        case 'advanced': return 'text-danger'
        default: return 'text-muted'
      }
    },

    getDifficultyVariant(difficulty) {
      switch (difficulty) {
        case 'beginner': return 'light-success'
        case 'intermediate': return 'light-warning'
        case 'advanced': return 'light-danger'
        default: return 'light-secondary'
      }
    },

    getStatusVariant(status) {
      switch (status) {
        case 'active': return 'light-success'
        case 'inactive': return 'light-warning'
        case 'archived': return 'light-secondary'
        default: return 'light-secondary'
      }
    },

    getStatusIcon(status) {
      switch (status) {
        case 'active': return 'PlayIcon'
        case 'inactive': return 'PauseIcon'
        case 'archived': return 'ArchiveIcon'
        default: return 'HelpCircleIcon'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
#test-pack-detail {
  .test-pack-header {
    box-shadow: 0 4px 25px 0 rgba(0,0,0,.1);
    border: none;

    .test-pack-icon-large {
      width: 80px;
      height: 80px;
      border-radius: 12px;
      background: #f8f9fa;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    h2 {
      font-size: 2rem;
      font-weight: 700;
      color: #2c2c2c;
    }

    .meta-info {
      margin-top: 1.5rem;

      .meta-item {
        small {
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 0.25rem;
        }
      }
    }

    .action-buttons {
      min-width: 180px;

      .btn {
        font-size: 0.875rem;
        padding: 0.5rem 1rem;
      }
    }
  }

  .stats-card {
    border: none;
    box-shadow: 0 4px 25px 0 rgba(0,0,0,.1);
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 8px 25px 0 rgba(0,0,0,.15);
    }

    .stats-icon {
      width: 50px;
      height: 50px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 1rem;
    }

    .stats-number {
      font-size: 2rem;
      font-weight: 700;
      margin-bottom: 0;
    }

    .stats-text {
      font-size: 0.9rem;
      opacity: 0.7;
    }
  }

  .card-header {
    background: linear-gradient(118deg, #7367F0, rgba(115, 103, 240, 0.7));
    color: white;
    border: none;

    h4 {
      color: white;
    }
  }
}
</style>
